import { useEffect, useState } from 'react';
import { fetchAPI } from '../utils/helpers';
import type { Language } from '../i18n/language';

export type SiteMode = 'live' | 'demo' | 'coming_soon' | 'maintenance';

type SiteModeResponse = {
  success?: boolean;
  data?: {
    web_app_mode?: SiteMode;
    web_app_mode_message?: string | null;
  };
};

const CACHE_KEY = 'englishbylevels:site-mode';
const CACHE_TTL_MS = 5 * 60 * 1000;
const DEFAULT_MODE: SiteMode = 'live';

const SITE_MODE_SYSTEM_MESSAGES: Record<Language, Partial<Record<SiteMode, string>>> = {
  tr: {
    demo: 'Çok Yakında Geliyoruz...',
    coming_soon: 'Çok Yakında Geliyoruz...',
    maintenance: 'Kısa bir bakım yapıyoruz. Birazdan tekrar buradayız.',
  },
  en: {
    demo: 'Coming soon...',
    coming_soon: 'Coming soon...',
    maintenance: 'We are doing a short maintenance. We will be back shortly.',
  },
};

const normalizeMessage = (message: string) => {
  return message
    .toLocaleLowerCase('tr-TR')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ş/g, 's')
    .replace(/ü/g, 'u')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
};

const getKnownMessageKey = (message: string): SiteMode | null => {
  const normalized = normalizeMessage(message);

  if (normalized.includes('cok yakinda') || normalized.includes('coming soon')) {
    return 'coming_soon';
  }

  if (normalized.includes('bakim') || normalized.includes('maintenance')) {
    return 'maintenance';
  }

  return null;
};

export const localizeSiteModeMessage = (
  message: string | null | undefined,
  language: Language
) => {
  if (!message?.trim()) return null;

  const knownMessageKey = getKnownMessageKey(message);
  if (knownMessageKey) {
    return SITE_MODE_SYSTEM_MESSAGES[language][knownMessageKey] ?? message;
  }

  return message;
};

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      ts: number;
      mode: SiteMode;
      message?: string | null;
    };
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCache = (mode: SiteMode, message?: string | null) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ ts: Date.now(), mode, message: message ?? null })
    );
  } catch {
    // Cache write is best-effort only.
  }
};

export const useSiteMode = () => {
  const [mode, setMode] = useState<SiteMode>(DEFAULT_MODE);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cached = readCache();
    if (cached) {
      setMode(cached.mode);
      setMessage(cached.message ?? null);
      setLoading(false);
      return;
    }

    const fetchConfig = async () => {
      try {
        const response = (await fetchAPI('learn-english/web-app-config', { skipAuth: true })) as SiteModeResponse;
        const nextMode = response?.data?.web_app_mode ?? DEFAULT_MODE;
        const nextMessage = response?.data?.web_app_mode_message ?? null;
        setMode(nextMode);
        setMessage(nextMessage);
        writeCache(nextMode, nextMessage);
      } catch (error) {
        console.warn('Site mode config fetch failed, defaulting to live.', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  useEffect(() => {
    document.documentElement.dataset.siteMode = mode;
  }, [mode]);

  return {
    mode,
    message,
    loading,
    isDemo: mode === 'demo',
    isRestricted: mode === 'coming_soon' || mode === 'maintenance',
  };
};
